import { useEffect, useState } from 'react';
import { FileText, Filter, Plus, Trash2 } from 'lucide-react';
import useDocumentStore from '@/stores/useDocumentStore';
import DocumentRow from './DocumentRow';
import DocumentAnalysisPanel from './DocumentAnalysisPanel';

const DOCUMENT_CATEGORIES = [
  {
    label: 'Propriété',
    types: ['titre_propriete', 'reglement_copropriete', 'comptes_rendus_ag', 'servitudes_privees'],
  },
  {
    label: 'Fiscal',
    types: ['taxe_fonciere', 'taxe_habitation'],
  },
  {
    label: 'Autorisations',
    types: ['permis_construire', 'daact', 'declaration_travaux'],
  },
  {
    label: 'Contentieux',
    types: ['contentieux', 'contentieux_locatif'],
  },
  {
    label: 'Technique',
    types: [
      'descriptif_technique', 'pv_reception_travaux', 'assurance_dommage_ouvrage',
      'rapport_assainissement', 'plans_niveaux', 'etat_surfaces',
    ],
  },
  {
    label: 'Locatif',
    types: [
      'etat_locatif', 'baux_avenants', 'convention_sous_location',
      'charges_locatives', 'taxes_assurances',
    ],
  },
  {
    label: 'Travaux',
    types: [
      'travaux_realises', 'travaux_previsionnels', 'estimation_travaux',
      'devis_travaux', 'factures_travaux',
    ],
  },
  {
    label: 'Diagnostics',
    types: [
      'ernmt', 'diagnostics_techniques', 'dpe', 'etude_pollution_sols',
      'attestation_piscine', 'mesurage_carrez',
    ],
  },
  {
    label: 'Vente / Location',
    types: ['compromis_vente', 'bail_location', 'etat_descriptif_division'],
  },
  {
    label: 'Copropriété',
    types: ['carnet_entretien', 'budget_previsionnel', 'appel_charges'],
  },
  {
    label: 'Plans',
    types: ['plan_bien', 'plan_cadastral', 'plan_masse'],
  },
  {
    label: 'Médias',
    types: ['photos'],
  },
];

const DOCUMENT_LABELS = {
  titre_propriete: 'Titre de propriété',
  reglement_copropriete: 'Règlement de copropriété',
  comptes_rendus_ag: 'Comptes rendus AG',
  servitudes_privees: 'Servitudes privées',
  taxe_fonciere: 'Taxe foncière',
  permis_construire: 'Permis de construire',
  daact: 'DAACT',
  contentieux: 'Contentieux',
  descriptif_technique: 'Descriptif technique',
  pv_reception_travaux: 'PV réception travaux',
  assurance_dommage_ouvrage: 'Assurance dommage-ouvrage',
  rapport_assainissement: 'Rapport assainissement',
  plans_niveaux: 'Plans des niveaux',
  etat_surfaces: 'État des surfaces',
  etat_locatif: 'État locatif',
  baux_avenants: 'Baux et avenants',
  convention_sous_location: 'Convention sous-location',
  contentieux_locatif: 'Contentieux locatif',
  charges_locatives: 'Charges locatives',
  taxes_assurances: 'Taxes et assurances',
  travaux_realises: 'Travaux réalisés',
  travaux_previsionnels: 'Travaux prévisionnels',
  ernmt: 'ERNMT',
  diagnostics_techniques: 'Diagnostics techniques',
  dpe: 'DPE',
  etude_pollution_sols: 'Étude pollution des sols',
  attestation_piscine: 'Attestation piscine',
  compromis_vente: 'Compromis de vente',
  bail_location: 'Bail de location',
  etat_descriptif_division: 'État descriptif de division',
  carnet_entretien: "Carnet d'entretien",
  budget_previsionnel: 'Budget prévisionnel',
  appel_charges: 'Appel de charges',
  mesurage_carrez: 'Mesurage loi Carrez',
  plan_bien: 'Plan du bien',
  plan_cadastral: 'Plan cadastral',
  plan_masse: 'Plan de masse',
  declaration_travaux: 'Déclaration de travaux',
  taxe_habitation: "Taxe d'habitation",
  estimation_travaux: 'Estimation de travaux',
  photos: 'Photographies',
  devis_travaux: 'Devis de travaux',
  factures_travaux: 'Factures de travaux',
};

const FILTERS = [
  { value: 'all', label: 'Tous' },
  { value: 'recu', label: 'Reçus' },
  { value: 'demande', label: 'Demandés' },
  { value: 'with_file', label: 'Avec fichier' },
  { value: 'analyzed', label: 'Analysés' },
];

export default function SectionDocuments({ expertiseId }) {
  const {
    documents,
    uploading,
    analyzing,
    fetchDocuments,
    addDocument,
    removeDocument,
    updateStatus,
    uploadFile,
    deleteFile,
    triggerAnalysis,
    validateAnalysis,
  } = useDocumentStore();

  const [filter, setFilter] = useState('all');
  const [analysisPanel, setAnalysisPanel] = useState(null); // document object or null
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDocLabel, setNewDocLabel] = useState('');

  useEffect(() => {
    if (expertiseId) {
      fetchDocuments(expertiseId);
    }
  }, [expertiseId, fetchDocuments]);

  // Compteurs
  const counts = {
    recu: documents.filter((d) => d.statut === 'recu').length,
    demande: documents.filter((d) => d.statut === 'demande').length,
    na: documents.filter((d) => d.statut === 'na').length,
    with_file: documents.filter((d) => d.filename_original).length,
    analyzed: documents.filter((d) => d.analyse_statut === 'done').length,
  };

  // Statistiques d'extraction / injection OCR (agrégées sur tous les documents).
  const extractionStats = documents.reduce(
    (acc, doc) => {
      const extracted = (doc.analyse_donnees_extraites?.fields || []).length;
      const injected = (doc.injected_fields || []).length;
      return {
        extracted: acc.extracted + extracted,
        injected: acc.injected + injected,
      };
    },
    { extracted: 0, injected: 0 },
  );

  // Séparer documents prédéfinis et personnalisés
  const predefinedDocs = documents.filter((d) => !d.type_document.startsWith('custom_'));
  const customDocs = documents.filter((d) => d.type_document.startsWith('custom_'));

  const docMap = Object.fromEntries(predefinedDocs.map((d) => [d.type_document, d]));

  const matchesFilter = (doc) => {
    if (!doc) return false;
    switch (filter) {
      case 'recu': return doc.statut === 'recu';
      case 'demande': return doc.statut === 'demande';
      case 'with_file': return !!doc.filename_original;
      case 'analyzed': return doc.analyse_statut === 'done';
      default: return true;
    }
  };

  const handleAddDocument = async () => {
    const label = newDocLabel.trim();
    if (!label) return;
    try {
      await addDocument(expertiseId, label);
      setNewDocLabel('');
      setShowAddForm(false);
    } catch {
      // error handled by store
    }
  };

  const handleRemoveDocument = async (docId) => {
    try {
      await removeDocument(docId);
    } catch {
      // error handled by store
    }
  };

  return (
    <div className="space-y-6">
      {/* Header avec compteurs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-carex-600" />
          <h2 className="text-lg font-semibold text-slate-900">Documents communiqués</h2>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full font-medium">
            {counts.recu} reçus
          </span>
          <span className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded-full font-medium">
            {counts.demande} demandés
          </span>
          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full font-medium">
            {counts.na} N/A
          </span>
        </div>
      </div>

      {/* Statistiques d'extraction / injection OCR */}
      {extractionStats.extracted > 0 && (
        <p className="text-xs text-slate-500 -mt-3">
          {extractionStats.extracted} champ
          {extractionStats.extracted > 1 ? 's' : ''} extrait
          {extractionStats.extracted > 1 ? 's' : ''},{' '}
          {extractionStats.injected} injecté
          {extractionStats.injected > 1 ? 's' : ''}
        </p>
      )}

      {/* Filtres */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-slate-400" />
        {FILTERS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => setFilter(value)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              filter === value
                ? 'bg-carex-100 text-carex-700 border border-carex-200'
                : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Catégories de documents */}
      <div className="space-y-6">
        {DOCUMENT_CATEGORIES.map((category) => {
          const categoryDocs = category.types
            .map((type) => ({ type, doc: docMap[type] }))
            .filter(({ doc }) => matchesFilter(doc));

          if (categoryDocs.length === 0) return null;

          return (
            <section key={category.label}>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">
                {category.label}
              </h3>
              <div className="space-y-1.5">
                {categoryDocs.map(({ type, doc }) => (
                  <DocumentRow
                    key={type}
                    document={doc}
                    label={DOCUMENT_LABELS[type] || type}
                    uploading={!!uploading[type]}
                    analyzing={!!analyzing[doc?.id]}
                    onStatusChange={(statut) => updateStatus(doc.id, statut)}
                    onUpload={(file) => uploadFile(expertiseId, type, file)}
                    onDeleteFile={() => deleteFile(doc.id)}
                    onAnalyze={() => triggerAnalysis(doc.id)}
                    onViewAnalysis={() => setAnalysisPanel(doc)}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Documents personnalisés */}
      {(customDocs.length > 0 || filter === 'all') && (
        <section>
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">
            Autres documents
          </h3>
          <div className="space-y-1.5">
            {customDocs.filter(matchesFilter).map((doc) => (
              <div key={doc.id} className="flex items-center gap-1.5">
                <div className="flex-1">
                  <DocumentRow
                    document={doc}
                    label={doc.label || doc.type_document}
                    uploading={!!uploading[doc.type_document]}
                    analyzing={!!analyzing[doc.id]}
                    onStatusChange={(statut) => updateStatus(doc.id, statut)}
                    onUpload={(file) => uploadFile(expertiseId, doc.type_document, file)}
                    onDeleteFile={() => deleteFile(doc.id)}
                    onAnalyze={() => triggerAnalysis(doc.id)}
                    onViewAnalysis={() => setAnalysisPanel(doc)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveDocument(doc.id)}
                  className="p-2 text-slate-400 hover:text-red-500 transition-colors shrink-0"
                  title="Supprimer ce document"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Bouton / formulaire d'ajout */}
          {showAddForm ? (
            <div className="flex items-center gap-2 mt-3">
              <input
                type="text"
                value={newDocLabel}
                onChange={(e) => setNewDocLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddDocument()}
                placeholder="Nom du document..."
                className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-carex-500 focus:border-carex-500"
                autoFocus
              />
              <button
                type="button"
                onClick={handleAddDocument}
                disabled={!newDocLabel.trim()}
                className="px-3 py-2 text-sm font-medium text-white bg-carex-600 hover:bg-carex-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Ajouter
              </button>
              <button
                type="button"
                onClick={() => { setShowAddForm(false); setNewDocLabel(''); }}
                className="px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Annuler
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1.5 mt-3 px-3 py-2 text-sm font-medium text-carex-600 bg-carex-50 border border-carex-200 rounded-lg hover:bg-carex-100 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Ajouter un document
            </button>
          )}
        </section>
      )}

      {/* Panneau d'analyse IA */}
      {analysisPanel && (
        <DocumentAnalysisPanel
          document={analysisPanel}
          label={DOCUMENT_LABELS[analysisPanel.type_document] || analysisPanel.type_document}
          onValidate={(acceptedFields) => validateAnalysis(analysisPanel.id, acceptedFields)}
          onClose={() => setAnalysisPanel(null)}
        />
      )}
    </div>
  );
}
